import _ from 'lodash';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import styles from './EditRoutine.module.scss';
import classNames from 'classnames';
import EmptyState from '../composites/EmptyState';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import SearchBar from '../atoms/SearchBar';
import ExerciseThumbnail from '../composites/ExerciseThumbnail';
import IconWithText from '../atoms/IconWithText';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import ExerciseGroupComponent from './ExerciseGroup';
import DropZone from './DropZone';
import { extractExerciseDragData } from './exerciseDragUtils';
import {
  calculateRoutineDurationMin,
  createTrainingSetFromExercise,
  getNumOfRoutineExercises,
} from '../../utils/dashboardUtils';
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
} from '@mui/material';
import EditName, { EditNameHandle } from '../composites/EditName';
import { locales, DragTypes } from '../../constants';
import {
  useSessionAction,
  useSessionData,
  useUnsavedChanges,
} from '../../providers';

import type {
  Exercise,
  ExerciseGroup,
  TrainingRoutine,
  SetGroup,
} from '../../types';

const exerciseGroupTexts = locales.en.components.workouts.exerciseGroup;
const texts = locales.en.components.workouts.editRoutine;

export interface EditRoutineDialogProps {
  onSubmit: (routine: TrainingRoutine) => Promise<boolean>;
  routine: TrainingRoutine;
  setRoutine: React.Dispatch<React.SetStateAction<TrainingRoutine>>;
  onClose: () => void;
  isNew?: boolean;
}

const EXERCISES_PER_SCROLL = 8;

const EditRoutine: React.FC<EditRoutineDialogProps> = ({
  onClose,
  onSubmit,
  routine,
  setRoutine,
  isNew = false,
}) => {
  const { setUnsavedChanges } = useUnsavedChanges();
  const { ensureExerciseLibraryLoaded } = useSessionAction();
  const { loginTrainer, gym, mergedExercises, mergedExercisesMap } =
    useSessionData();
  const [loading, setLoading] = useState(false);
  const [editDescriptionActive, setEditDescriptionActive] = useState(false);
  const descriptionRef = useRef<HTMLInputElement>(null);
  // name editing handled by EditName component
  const editNameRef = useRef<EditNameHandle>(null);
  const exerciseListRef = useRef<HTMLDivElement>(null);
  const [description, setDescription] = useState(routine.description || '');
  const [name, setName] = useState(routine.name || '');
  const [numOfExercisesToShow, setNumOfExercisesToShow] =
    useState<number>(EXERCISES_PER_SCROLL);
  const [exercisesError, setExercisesError] = useState(false);
  const nameStateRef = useRef(name);
  const descriptionStateRef = useRef(description);
  const routineStateRef = useRef(routine);

  useEffect(() => {
    nameStateRef.current = name;
  }, [name]);
  useEffect(() => {
    descriptionStateRef.current = description;
  }, [description]);
  useEffect(() => {
    routineStateRef.current = routine;
  }, [routine]);

  useEffect(() => {
    if (routine.exerciseGroups.length > 0 && exercisesError) {
      setExercisesError(false);
    }
  }, [routine.exerciseGroups.length, exercisesError]);

  const updateRoutine = (
    updater:
      | TrainingRoutine
      | ((prev: TrainingRoutine) => TrainingRoutine),
  ) => {
    const next =
      typeof updater === 'function' ? updater(routineStateRef.current) : updater;
    routineStateRef.current = next;
    setRoutine(next);
  };

  // commit handled via EditName
  const [topDropZoneActive, setTopDropZoneActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropZoneVisible, setDropZoneVisible] = useState(false);
  const trainerId = loginTrainer?.id;

  useEffect(() => {
    void ensureExerciseLibraryLoaded();
  }, [ensureExerciseLibraryLoaded]);

  useEffect(() => {
    const handleGlobalDragEnd = () => {
      window.dispatchEvent(new CustomEvent('globalDragEnd'));
    };
    window.addEventListener('dragend', handleGlobalDragEnd);
    return () => window.removeEventListener('dragend', handleGlobalDragEnd);
  }, []);

  const initialRoutineRef = useRef<{
    name: string;
    description: string;
    exerciseGroups: ExerciseGroup[];
  } | null>(null);

  useEffect(() => {
    if (!initialRoutineRef.current) {
      initialRoutineRef.current = {
        name: routine.name,
        description: routine.description ?? '',
        exerciseGroups: _.cloneDeep(routine.exerciseGroups),
      };
    }
  }, []);

  useEffect(() => {
    setUnsavedChanges({
      hasUnsavedChanges: () => {
        if (isNew) {
          return routineStateRef.current.exerciseGroups.length > 0;
        }
        const currentName = nameStateRef.current;
        const currentDescription = descriptionStateRef.current;
        const currentRoutine = routineStateRef.current;

        const nameChanged =
          currentName !== (initialRoutineRef.current?.name ?? '');
        const descriptionChanged =
          currentDescription !== (initialRoutineRef.current?.description ?? '');
        const groupsChanged = !_.isEqual(
          currentRoutine.exerciseGroups,
          initialRoutineRef.current?.exerciseGroups,
        );
        return nameChanged || descriptionChanged || groupsChanged;
      },
      save: async () => {
        await onSubmit({
          ...routineStateRef.current,
          name: nameStateRef.current,
          description: descriptionStateRef.current,
        });
      },
      proceedAfterSave: true,
      canSave: () =>
        Boolean(nameStateRef.current?.trim()) &&
        routineStateRef.current.exerciseGroups.length > 0,
    });
    return () => setUnsavedChanges(null);
  }, [isNew, setUnsavedChanges, onSubmit]);

  const filteredExercises = useMemo(() => {
    return mergedExercises
      .filter(({ name, imageUrl }) => name && imageUrl)
      .filter(({ name }) =>
        name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a, b) => {
        if (a.trainerId === trainerId && b.trainerId !== trainerId) return -1;
        if (a.trainerId !== trainerId && b.trainerId === trainerId) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [mergedExercises, searchQuery, trainerId]);

  const handleExercisesScroll = useMemo(
    () =>
      _.debounce(() => {
        const exerciseListElement = exerciseListRef.current;
        if (!exerciseListElement) return;
        const scrollHeight = exerciseListElement.scrollHeight;
        const currentScroll = exerciseListElement.scrollTop;
        const clientHeight = exerciseListElement.clientHeight;
        const denominator = scrollHeight - clientHeight || 1;
        const currentRatio = currentScroll / denominator;
        if (currentRatio > 0.8) {
          setNumOfExercisesToShow((prevNumOfExercisesToShow) =>
            Math.min(
              filteredExercises.length,
              prevNumOfExercisesToShow + EXERCISES_PER_SCROLL,
            ),
          );
        }
      }, 300),
    [filteredExercises.length],
  );

  useEffect(() => {
    return () => {
      handleExercisesScroll.cancel();
    };
  }, [handleExercisesScroll]);

  const getRoutineInfos = () => {
    return [
      {
        text: `${calculateRoutineDurationMin(routine)} min`,
        icon: (
          <AccessTimeOutlinedIcon style={{ color: 'var(--color-text-3)' }} />
        ),
      },
      {
        text: `${getNumOfRoutineExercises(routine)} exercises`,
        icon: (
          <FitnessCenterOutlinedIcon style={{ color: 'var(--color-text-3)' }} />
        ),
      },
    ];
  };

  const updateExerciseGroup = (
    updatedExerciseGroup: ExerciseGroup,
    groupIndex: number,
  ) => {
    updateRoutine((prevRoutine) => {
      const exerciseGroups = prevRoutine.exerciseGroups
        .map((group, index) =>
          groupIndex === index ? updatedExerciseGroup : group,
        )
        .filter(({ sets }) => sets.length > 0);
      return {
        ...prevRoutine,
        exerciseGroups,
      };
    });
  };

  const handleSetGroupDropFromExerciseGroup = (
    droppedExercise: Exercise,
    exerciseGroupIndexToRemove: number,
    groupIndexToRemove: number,
    newExerciseGroupIndex: number,
    setGroup: SetGroup,
    trainerNote?: string,
  ) => {
    updateRoutine((prevRoutine) => {
      const newExerciseGroup = {
        name: droppedExercise.name,
        sets: [[...setGroup]],
        trainerNotes: trainerNote !== undefined ? [trainerNote] : [],
      };
      const updatedExerciseGroups = prevRoutine.exerciseGroups.map(
        (group, i) =>
          exerciseGroupIndexToRemove === i
            ? {
              ...group,
              sets: group.sets.filter(
                (_, index) => index !== groupIndexToRemove,
              ),
            }
            : group,
      );
      const newExerciseGroups = [
        ...updatedExerciseGroups.slice(0, newExerciseGroupIndex),
        newExerciseGroup,
        ...updatedExerciseGroups.slice(newExerciseGroupIndex),
      ];

      return {
        ...prevRoutine,
        exerciseGroups: newExerciseGroups,
      };
    });
  };

  const handleSetGroupMoveToExistingExerciseGroup = (
    newExerciseGroupIndex: number,
    draggedExerciseGroupIndex: number,
    draggedSetGroupIndex: number,
    draggedSetGroup: SetGroup,
    targetSetIndex?: number,
  ) => {
    updateRoutine((prevRoutine) => {
      const updatedExerciseGroups = prevRoutine.exerciseGroups.map(
        (group, i) =>
          draggedExerciseGroupIndex === i
            ? {
              ...group,
              sets: group.sets.filter(
                (_, index) => index !== draggedSetGroupIndex,
              ),
            }
            : newExerciseGroupIndex === i
              ? {
                ...group,
                sets: targetSetIndex !== undefined
                  ? [
                    ...group.sets.slice(0, targetSetIndex),
                    draggedSetGroup,
                    ...group.sets.slice(targetSetIndex)
                  ]
                  : [...group.sets, draggedSetGroup],
              }
              : group,
      );

      return {
        ...prevRoutine,
        exerciseGroups: updatedExerciseGroups,
      };
    });
  };

  const handleUnifyExerciseGroups = (
    draggedExerciseGroup: ExerciseGroup,
    draggedExerciseGroupIndex: number,
    targetExerciseGroupIndex: number,
    targetSetIndex?: number,
  ) => {
    updateRoutine((prevRoutine) => {
      const updatedExerciseGroups = prevRoutine.exerciseGroups.map(
        (group, i) =>
          draggedExerciseGroupIndex === i
            ? {
              ...group,
              sets: [],
            }
            : targetExerciseGroupIndex === i
              ? {
                ...group,
                sets: targetSetIndex !== undefined
                  ? [
                    ...group.sets.slice(0, targetSetIndex),
                    ...draggedExerciseGroup.sets,
                    ...group.sets.slice(targetSetIndex)
                  ]
                  : [...group.sets, ...draggedExerciseGroup.sets],
              }
              : group,
      );

      const newExerciseGroups = updatedExerciseGroups.filter(
        ({ sets }) => sets.length > 0,
      );

      return {
        ...prevRoutine,
        exerciseGroups: newExerciseGroups,
      };
    });
  };

  const handleDropExerciseOnExerciseGroup = (
    exercise: Exercise,
    indexToInsertExerciseGroup: number,
  ) => {
    // needs to add a new exercise group with the exercise to the routine in the specified index. If the index is -1, it should add the exercise group to the end of the routine. if the index location already contains an exercise group, push this new one bwteen the two.
    updateRoutine((prevRoutine) => {
      const newExerciseGroup = {
        name: exercise.name,
        sets: [[createTrainingSetFromExercise(exercise, gym?.id || '')]],
        trainerNotes: [],
      };
      const newExerciseGroups = [
        ...prevRoutine.exerciseGroups.slice(0, indexToInsertExerciseGroup),
        newExerciseGroup,
        ...prevRoutine.exerciseGroups.slice(indexToInsertExerciseGroup),
      ];
      return {
        ...prevRoutine,
        exerciseGroups: newExerciseGroups,
      };
    });
  };

  const handleExerciseDropOnSets = (
    droppedExercise: Exercise,
    exerciseGroupIndex: number,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => {
    updateRoutine((prevRoutine) => {
      const newTrainingSet = createTrainingSetFromExercise(
        droppedExercise,
        gym?.id || '',
      );
      const updatedExerciseGroups = prevRoutine.exerciseGroups.map(
        (group, i) =>
          exerciseGroupIndex === i
            ? {
              ...group,
              sets: [...group.sets, [newTrainingSet]],
            }
            : group,
      );
      if (
        exerciseGroupIndexToRemove !== undefined &&
        !groupIndexToRemove &&
        groupIndexToRemove !== 0
      ) {
        const newExerciseGroups = updatedExerciseGroups.filter(
          (_, i) => i !== exerciseGroupIndexToRemove,
        );
        return {
          ...prevRoutine,
          exerciseGroups: newExerciseGroups,
        };
      }
      if (
        groupIndexToRemove !== undefined &&
        exerciseGroupIndexToRemove !== undefined
      ) {
        updatedExerciseGroups[exerciseGroupIndexToRemove] = {
          ...updatedExerciseGroups[exerciseGroupIndexToRemove],
          sets: updatedExerciseGroups[exerciseGroupIndexToRemove].sets.filter(
            (_, index) => index !== groupIndexToRemove,
          ),
        };
      }
      return {
        ...prevRoutine,
        exerciseGroups: updatedExerciseGroups,
      };
    });
  };

  /** Position-aware insertion: inserts at a specific index within a group's sets */
  const handleExerciseDropOnSetsAtPosition = (
    droppedExercise: Exercise,
    exerciseGroupIndex: number,
    targetSetIndex: number,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => {
    setRoutine((prevRoutine) => {
      const newTrainingSet = createTrainingSetFromExercise(
        droppedExercise,
        gym?.id || '',
      );
      let updatedExerciseGroups = prevRoutine.exerciseGroups.map(
        (group, i) => {
          if (exerciseGroupIndex === i) {
            const newSets = [...group.sets];
            newSets.splice(targetSetIndex, 0, [newTrainingSet]);
            return { ...group, sets: newSets };
          }
          return group;
        },
      );
      // Remove from source group if needed
      if (
        exerciseGroupIndexToRemove !== undefined &&
        groupIndexToRemove !== undefined
      ) {
        // Adjust the source index: if source is same group and after target, account for the insertion
        updatedExerciseGroups = updatedExerciseGroups.map((group, i) => {
          if (i === exerciseGroupIndexToRemove) {
            const adjustedRemoveIndex =
              exerciseGroupIndexToRemove === exerciseGroupIndex &&
                groupIndexToRemove >= targetSetIndex
                ? groupIndexToRemove + 1
                : groupIndexToRemove;
            return {
              ...group,
              sets: group.sets.filter(
                (_, index) => index !== adjustedRemoveIndex,
              ),
            };
          }
          return group;
        });
      } else if (
        exerciseGroupIndexToRemove !== undefined &&
        groupIndexToRemove === undefined
      ) {
        updatedExerciseGroups = updatedExerciseGroups.filter(
          (_, i) => i !== exerciseGroupIndexToRemove,
        );
      }
      // Clean up empty groups
      updatedExerciseGroups = updatedExerciseGroups.filter(
        ({ sets }) => sets.length > 0,
      );
      return {
        ...prevRoutine,
        exerciseGroups: updatedExerciseGroups,
      };
    });
  };

  const handleDropOnRoutine = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/json');
    const parsedData = JSON.parse(data);

    switch (parsedData.type) {
      case DragTypes.GroupSetReorder:
        {
          const exerciseGroupIndexToRemove = Number(
            parsedData.exerciseGroupIndex,
          );
          const groupIndexToRemove = Number(parsedData.groupSetIndex);
          if (
            Number.isNaN(exerciseGroupIndexToRemove) ||
            Number.isNaN(groupIndexToRemove)
          ) {
            break;
          }

          const trainerNote =
            parsedData.exerciseGroup?.trainerNotes?.[groupIndexToRemove];

          handleSetGroupDropFromExerciseGroup(
            parsedData.exercise,
            exerciseGroupIndexToRemove,
            groupIndexToRemove,
            routine.exerciseGroups.length,
            parsedData.setGroup,
            trainerNote,
          );
        }
        break;
      case DragTypes.DragExercise:
        const exercise = extractExerciseDragData(e);
        updateRoutine((prevRoutine) => {
          const newExerciseGroup = {
            name: exercise.name,
            sets: [[createTrainingSetFromExercise(exercise, gym?.id || '')]],
            trainerNotes: [],
          };
          const newExerciseGroups = [
            ...prevRoutine.exerciseGroups,
            newExerciseGroup,
          ];
          return {
            ...prevRoutine,
            exerciseGroups: newExerciseGroups,
          };
        });
        break;
      default:
        console.error(`unknown ${parsedData.type} drag type`);
        break;
    }

    setDropZoneVisible(false);
  };

  const handleDragOverRoutine = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneVisible(true);
  };

  const handleDragOverRoutineEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDropZoneVisible(false);
  };

  const moveExerciseGroup = (
    groupOriginIndex: number,
    groupTargetIndex: number,
  ) => {
    if (
      !routine.exerciseGroups[groupOriginIndex] ||
      groupTargetIndex < 0 ||
      groupTargetIndex > routine.exerciseGroups.length // target can be equal to length
    ) {
      return;
    }

    let adjustedTarget = groupTargetIndex;
    if (groupOriginIndex < groupTargetIndex) {
      adjustedTarget -= 1;
    }

    updateRoutine((prevRoutine) => {
      const newExerciseGroups = [...prevRoutine.exerciseGroups];
      const [movedGroup] = newExerciseGroups.splice(groupOriginIndex, 1);
      newExerciseGroups.splice(adjustedTarget, 0, movedGroup);
      return {
        ...prevRoutine,
        exerciseGroups: newExerciseGroups,
      };
    });
  };

  const handleSubmit = async () => {
    const isNameValid = editNameRef.current?.triggerValidation() ?? Boolean(name);
    const hasExercises = routine.exerciseGroups.length > 0;

    setExercisesError(!hasExercises);

    if (!isNameValid || !hasExercises) {
      return;
    }

    setLoading(true);
    const success = await onSubmit(routine);
    if (success) {
      onClose();
    } else {
      setErrorMessage('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className={styles.editRoutine}>
      <div
        className={classNames(styles.editRoutineContent, styles.leftContent)}
      >
        <h1 className={styles.title}>Drag to add exercise to workout</h1>
        <SearchBar
          className={styles.editRoutineSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search Exercises..."
        />
        <div
          onScroll={handleExercisesScroll}
          className={styles.exerciseList}
          ref={exerciseListRef}
        >
          <div className={styles.exercises}>
            {filteredExercises
              .slice(0, numOfExercisesToShow)
              .map((exercise) => (
                <div key={exercise.id} className={styles.exerciseItem}>
                  <ExerciseThumbnail
                    exercise={exercise}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({
                          exercise,
                          type: DragTypes.DragExercise,
                        }),
                      );
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.editRoutineContent, styles.rightContent)}
      >
        <div className={styles.rightContentHeader}>
          <div className={styles.routineName}>
            <EditName
              ref={editNameRef}
              value={name}
              placeholder={texts.titlePlaceholder}
              onChange={setName}
              onCommit={(next) => {
                updateRoutine((prevRoutine) => ({
                  ...prevRoutine,
                  name: next,
                }));
              }}
            />
          </div>
          <div className={styles.routineInfos}>
            {getRoutineInfos().map(({ text, icon }) => (
              <IconWithText key={text} icon={icon} text={text} />
            ))}
          </div>
        </div>
        <div
          onDrop={handleDropOnRoutine}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropZoneVisible(true);
          }}
          onDragEnter={handleDragOverRoutine}
          onDragLeave={handleDragOverRoutineEnd}
          className={styles.routineContent}
        >
          {routine.exerciseGroups.length === 0 ? (
            <EmptyState
              icon={
                <FitnessCenterOutlinedIcon
                  style={{
                    color: exercisesError
                      ? 'var(--color-error)'
                      : 'var(--color-text-3)',
                  }}
                />
              }
              text={
                exercisesError
                  ? 'Please add at least one exercise to save this workout.'
                  : 'No exercises yet. Add exercises to start building your routine.'
              }
            />
          ) : (
            <>
              <div className={styles.routineDescription}>
                <div className={styles.routineDescriptionHeader}>
                  <span className={styles.routineDescriptionTitle}>
                    INSTRUCTIONS
                  </span>
                  <div
                    className={styles.routineDescriptionEdit}
                    onClick={() => {
                      setEditDescriptionActive(true);
                      descriptionRef.current?.select();
                    }}
                  >
                    <DriveFileRenameOutlineOutlinedIcon
                      style={{ color: 'var(--color-text-3)' }}
                    />
                  </div>
                </div>
                <div className={styles.routineDescriptionContent}>
                  <TextField
                    placeholder={texts.descriptionPlaceholder}
                    fullWidth
                    inputRef={descriptionRef}
                    color="primary"
                    name="workoutDescription"
                    value={description}
                    className={classNames(styles.descriptionTextField, {
                      [styles.editedDescription]: editDescriptionActive,
                    })}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setDescription(e.target.value)
                    }
                    onFocus={() => setEditDescriptionActive(true)}
                    onBlur={() => {
                      setEditDescriptionActive(false);
                      updateRoutine((prevRoutine) => ({
                        ...prevRoutine,
                        description,
                      }));
                    }}
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        color: 'var(--color-text-4)',
                        fontStyle: 'italic', // Additional styling
                        fontSize: '12px',
                      },
                    }}
                  />
                </div>
              </div>
              <div className={styles.routineGroups}>
                <DropZone
                  className={classNames(styles.interGroupDropZone, {
                    [styles.interGroupDropZoneActive]: topDropZoneActive,
                  })}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setTopDropZoneActive(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setTopDropZoneActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setTopDropZoneActive(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTopDropZoneActive(false);
                    const data = e.dataTransfer.getData('application/json');
                    const parsedData = JSON.parse(data);
                    switch (parsedData.type) {
                      case DragTypes.DragExercise:
                        handleDropExerciseOnExerciseGroup(parsedData.exercise, 0);
                        break;
                      case DragTypes.GroupSetReorder:
                        handleSetGroupDropFromExerciseGroup(
                          parsedData.exercise,
                          Number(parsedData.exerciseGroupIndex),
                          Number(parsedData.groupSetIndex),
                          0,
                          parsedData.setGroup,
                          parsedData.exerciseGroup?.trainerNotes?.[Number(parsedData.groupSetIndex)],
                        );
                        break;
                      case DragTypes.ExerciseGroupReorder:
                        moveExerciseGroup(Number(parsedData.exerciseGroupIndex), 0);
                        break;
                    }
                  }}
                  text={exerciseGroupTexts.dropToCreateGroup}
                />
                {routine.exerciseGroups.map((group, index) => (
                  <React.Fragment key={`group-${index}`}>
                    <ExerciseGroupComponent
                      moveExerciseGroup={moveExerciseGroup}
                      trainerMergedExercises={mergedExercisesMap}
                      exerciseGroup={group}
                      exerciseGroupIndex={index}
                      onSetGroupMoveToExistingExerciseGroup={(
                        draggedExerciseGroupIndex,
                        draggedSetGroupIndex,
                        setGroup,
                        targetSetIndex,
                      ) => {
                        handleSetGroupMoveToExistingExerciseGroup(
                          index,
                          draggedExerciseGroupIndex,
                          draggedSetGroupIndex,
                          setGroup,
                          targetSetIndex,
                        );
                      }}
                      onUnifyExerciseGroups={handleUnifyExerciseGroups}
                      onDropExerciseAfterExerciseGroup={
                        handleDropExerciseOnExerciseGroup
                      }
                      onExerciseDrop={(
                        exercise,
                        exerciseGroupIndexToRemove,
                        groupIndexToRemove,
                      ) =>
                        handleExerciseDropOnSets(
                          exercise,
                          index,
                          exerciseGroupIndexToRemove,
                          groupIndexToRemove,
                        )
                      }
                      onExerciseDropAtPosition={(
                        exercise,
                        targetSetIndex,
                        exerciseGroupIndexToRemove,
                        groupIndexToRemove,
                      ) =>
                        handleExerciseDropOnSetsAtPosition(
                          exercise,
                          index,
                          targetSetIndex,
                          exerciseGroupIndexToRemove,
                          groupIndexToRemove,
                        )
                      }
                      onSetGroupDropFromExerciseGroup={(
                        droppedExercise,
                        exerciseGroupIndexToRemove,
                        groupIndexToRemove,
                        newExerciseGroupIndex,
                        setGroup,
                        trainerNote,
                      ) => {
                        handleSetGroupDropFromExerciseGroup(
                          droppedExercise,
                          exerciseGroupIndexToRemove,
                          groupIndexToRemove,
                          newExerciseGroupIndex,
                          setGroup,
                          trainerNote,
                        );
                      }}
                      updateExerciseGroup={(updatedExerciseGroup) =>
                        updateExerciseGroup(updatedExerciseGroup, index)
                      }
                    />
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
          <DropZone
            className={classNames(styles.dropZone, {
              [styles.dropZoneVisible]: dropZoneVisible,
            })}
            onDragOver={(e) => {
              e.preventDefault();
              setDropZoneVisible(true);
            }}
            onDragEnter={(e) => { e.preventDefault(); setDropZoneVisible(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDropZoneVisible(false); }}
            onDrop={handleDropOnRoutine}
            text={exerciseGroupTexts.dropToCreateGroup}
          />
        </div>
        <div className={styles.rightContentFooter}>
          <Button
            className={classNames(styles.submit, styles.action)}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="info" />
            ) : (
              <span className={styles.submitText}>Save Workout</span>
            )}
          </Button>
          <Button
            className={classNames(styles.cancel, styles.action)}
            onClick={onClose}
            disabled={loading}
          >
            <span className={styles.cancelText}>Cancel</span>
          </Button>
        </div>
      </div>
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setErrorMessage('')}
        >
          <Alert onClose={() => setErrorMessage('')} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default EditRoutine;
